import React, { useEffect, useState } from 'react';
import {
  Input,
  Badge, Box, Button, Checkbox, Divider, HStack, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalFooter, ModalHeader, ModalOverlay, Skeleton, Stack, Text, useToast, useColorModeValue,
} from '@chakra-ui/react';
import { FiCreditCard } from 'react-icons/fi';
import axios from 'axios';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

// item 298 — auto-pay enrollment for a recurring subscription. Reads the LIVE Stripe
// state: if the client has an ACH bank on file (they've paid an invoice by bank debit),
// an admin can — with the client's permission confirmed — flip the subscription from
// emailed invoices to automatic charges against that bank.
interface AutoPayState {
  available: boolean;
  enrolled?: boolean;
  hasBank?: boolean;
  bankName?: string | null;
  last4?: string | null;
  // item 1340 — the enrollment's audit stamp: who approved auto-pay, when, which door
  enrolledAt?: string | null;
  enrolledBy?: string | null;
  enrolledVia?: string | null;
  authorizationMemo?: string | null;
}

const AutoPayModal: React.FC<{
  recurringId: string | null;
  onClose: () => void;
}> = ({ recurringId, onClose }) => {
  const toast = useToast();
  const [state, setState] = useState<AutoPayState | null>(null);
  const [permission, setPermission] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  // item 1341 — the typed authorization memo (goes to the sub ledger + Stripe's memo)
  const [memo, setMemo] = useState('');
  const [linking, setLinking] = useState(false);
  // item 958 — light-mode contrast: the bank card's whiteAlpha edge/fill vanished on light
  const cardEdge = useColorModeValue('blackAlpha.200', 'whiteAlpha.300');
  const cardFill = useColorModeValue('blackAlpha.50', 'whiteAlpha.50');

  // item 727 (Chad) — CLIENT SELF-SERVE: fetch a Stripe Customer Portal link the client opens to
  // add their bank + turn on auto-pay themselves (no admin flip). Copies it to the clipboard to send.
  const copySelfServeLink = async () => {
    if (!recurringId) return;
    setLinking(true);
    try {
      const { data } = await axios.post(`${apiUrl()}payment/portal/${recurringId}`);
      await navigator.clipboard.writeText(data.url);
      toast({ status: 'success', title: 'Self-serve link copied', description: 'Send it to the client — they can add their bank + turn on auto-pay themselves.' });
    } catch (e: any) {
      toast({ status: 'error', title: 'Could not create the link', description: e?.response?.data ?? 'Is the Stripe Customer Portal configured?' });
    } finally {
      setLinking(false);
    }
  };

  // item 782 (Chad) — EMAIL the client an auto-pay SETUP link (Stripe Checkout, setup
  // mode): they confirm/add their bank and authorize automatic payments themselves; the
  // webhook flips the subscription and sends the confirmation. Unlike the portal link,
  // completing THIS link actually turns auto-pay on.
  const [sendingLink, setSendingLink] = useState(false);
  const emailSetupLink = async () => {
    if (!recurringId) return;
    setSendingLink(true);
    try {
      const { data } = await axios.post(`${apiUrl()}payment/autopay-link/${recurringId}`);
      toast({ status: 'success', title: 'Setup link emailed', description: `Sent to ${data?.sent ?? 'the billing contact'} — auto-pay turns on automatically once they approve.` });
    } catch (e: any) {
      toast({ status: 'error', title: 'Could not send the link', description: e?.response?.data ?? 'Try again.' });
    } finally {
      setSendingLink(false);
    }
  };

  useEffect(() => {
    setState(null);
    setPermission(false);
    if (!recurringId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${apiUrl()}payment/autopay/${recurringId}`);
        setState(data);
      } catch {
        setState({ available: false });
      }
    })();
  }, [recurringId]);

  // item 752 (Chad) — undo auto-pay: back to emailed invoices; the bank stays saved at Stripe
  const [disabling, setDisabling] = useState(false);
  const disable = async () => {
    if (!recurringId) return;
    setDisabling(true);
    try {
      await axios.post(`${apiUrl()}payment/autopay/${recurringId}/disable`);
      setState((s) => (s ? { ...s, enrolled: false } : s));
      toast({ status: 'success', title: 'Auto-pay turned off', description: 'Future cycles go back to emailed invoices. The bank stays on file for re-enrolling.' });
    } catch (e: any) {
      toast({ status: 'error', title: 'Could not turn off auto-pay', description: e?.response?.data ?? 'Try again.' });
    } finally {
      setDisabling(false);
    }
  };

  const enroll = async () => {
    if (!recurringId) return;
    setEnrolling(true);
    try {
      await axios.post(`${apiUrl()}payment/autopay/${recurringId}`, { memo: memo.trim() || null });
      setState((s) => (s ? { ...s, enrolled: true } : s));
      toast({ status: 'success', title: 'Auto-pay enrolled', description: 'Future cycles charge the bank on file automatically.' });
      onClose();
    } catch (e: any) {
      toast({ status: 'error', title: 'Enrollment failed', description: e?.response?.data ?? 'Try again.' });
    } finally {
      setEnrolling(false);
    }
  };

  return (
    // item 750 (Chad) — the modal was a cramped button pile-up ("Copy client self-serve link"
    // colliding with "Enroll in auto-pay"). Restructured: roomier (lg), the bank-on-file as a
    // clear card, and the TWO pathways laid out as separate sections — "Enroll now (you)" with
    // its consent checkbox + button together, an OR divider, then "Let the client do it" with
    // its own explanation + copy-link button. Footer holds only Close.
    <Modal isOpen={!!recurringId} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Auto-pay (ACH)</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!state && <Skeleton h="120px" />}
          {state && !state.available && (
            <Text fontSize="sm" opacity={0.8}>
              This schedule isn&apos;t connected to a payment subscription yet — auto-pay
              becomes available for subscriptions created after the payments integration.
            </Text>
          )}
          {state?.available && state.enrolled && (
            <Stack spacing={3} pb={2}>
              <Badge colorScheme="green" w="fit-content">AUTO-PAY ON</Badge>
              <Text fontSize="sm">
                {`Each cycle charges the bank on file${state.last4 ? ` (${state.bankName ?? 'bank'} ••${state.last4})` : ''} automatically.`}
              </Text>
              {/* item 1340 (Chad) — who approved auto-pay, when, and through which door */}
              {(state.enrolledBy || state.enrolledAt) && (
                <Text fontSize="xs" opacity={0.65}>
                  {`Enrolled${state.enrolledBy ? ` by ${state.enrolledBy}` : ''}${state.enrolledAt ? ` on ${new Date(state.enrolledAt).toLocaleDateString([], { month: 'numeric', day: 'numeric', year: 'numeric' })}` : ''}${state.enrolledVia ? ` · ${state.enrolledVia}` : ''}.`}
                </Text>
              )}
              {/* item 1341 — the typed authorization memo (also on the Stripe sub's memo) */}
              {state.authorizationMemo && (
                <Text fontSize="xs" opacity={0.65} fontStyle="italic">{`“${state.authorizationMemo}”`}</Text>
              )}
              {/* item 752 (Chad) — undo: back to emailed invoices (bank stays saved for re-enroll) */}
              <Button variant="outline" colorScheme="red" alignSelf="flex-start" size="sm" isLoading={disabling} onClick={disable}>
                Turn off auto-pay (back to emailed invoices)
              </Button>
            </Stack>
          )}
          {state?.available && !state.enrolled && (
            <Stack spacing={5} pb={2}>
              {/* the bank on file (or the lack of one) as a clear card */}
              <HStack
                px={4}
                py={3}
                borderWidth="1px"
                borderRadius="lg"
                borderColor={cardEdge}
                bg={cardFill}
                spacing={3}
              >
                <Box fontSize="20px" opacity={0.8}><FiCreditCard /></Box>
                {state.hasBank ? (
                  <>
                    <Text fontSize="sm" fontWeight="semibold">
                      {`${state.bankName ?? 'Bank account'} ••${state.last4 ?? ''}`}
                    </Text>
                    <Badge colorScheme="green" variant="subtle">BANK ON FILE</Badge>
                  </>
                ) : (
                  <>
                    <Text fontSize="sm" fontWeight="semibold">No bank on file yet</Text>
                    <Badge colorScheme="orange" variant="subtle">EMAILED INVOICES</Badge>
                  </>
                )}
              </HStack>

              {/* pathway 1 — admin enrolls now (needs a bank + the client's OK) */}
              {state.hasBank && (
                <Stack spacing={3}>
                  <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.06em" opacity={0.6}>
                    Enroll now (you)
                  </Text>
                  <Text fontSize="sm" opacity={0.85}>
                    Switches this subscription from emailed invoices to automatic charges
                    against the bank on file, starting next cycle.
                  </Text>
                  <Checkbox isChecked={permission} onChange={(e) => setPermission(e.target.checked)}>
                    <Text fontSize="sm">The client has given permission to charge this account automatically.</Text>
                  </Checkbox>
                  {/* item 1341 (Chad) — the authorization note: lands in the sub ledger AND on the
                  Stripe subscription's memo. e.g. "Approved Auto-Pay via Ron on 8-31-2026 via
                  phone call." */}
                  <Input
                    size="sm"
                    mt={2}
                    placeholder="Authorization note — e.g. Approved auto-pay via Ron on 8/31/2026 by phone"
                    value={memo}
                    onChange={(e) => setMemo(e.target.value)}
                  />
                  <Button
                    colorScheme="green"
                    alignSelf="flex-start"
                    isDisabled={!permission}
                    isLoading={enrolling}
                    onClick={enroll}
                  >
                    Enroll in auto-pay
                  </Button>
                </Stack>
              )}

              {state.hasBank && (
                <HStack>
                  <Divider />
                  <Text fontSize="xs" opacity={0.5} flexShrink={0}>OR</Text>
                  <Divider />
                </HStack>
              )}

              {/* pathway 2 — the client self-serves via the Stripe portal (item 727) */}
              <Stack spacing={3}>
                <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.06em" opacity={0.6}>
                  Let the client do it
                </Text>
                <Text fontSize="sm" opacity={0.85}>
                  {state.hasBank
                    ? 'Email the setup link — the client confirms their bank and approves; auto-pay turns on by itself the moment they do.'
                    : 'Email the setup link — the client adds their bank and approves; auto-pay turns on by itself the moment they do.'}
                </Text>
                <HStack spacing={3}>
                  <Button colorScheme="green" variant="outline" isLoading={sendingLink} onClick={emailSetupLink}>
                    Email auto-pay setup link
                  </Button>
                  {/* item 727's portal link stays as the manage-payment-methods fallback */}
                  <Button variant="ghost" size="sm" isLoading={linking} onClick={copySelfServeLink}>
                    Copy portal link instead
                  </Button>
                </HStack>
              </Stack>
            </Stack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AutoPayModal;
